/**
 * ContalinkInvoiceModal - Registra una factura en Contalink
 *
 * POST /invoices/{id}/contalink (multipart/form-data)
 */

import { faFilePdf, faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostContalinkInvoice } from '../../hooks/usePostContalinkInvoice';
import { Invoice } from '../../models/Invoices';

export interface ContalinkInvoiceModalRef {
  open: (invoice: Invoice, onSuccess?: () => void) => void;
}

interface ContalinkFormData {
  erp_number: string;
  erp_id: string;
  grand_total: string;
  pdf: File | null;
}

export const ContalinkInvoiceModal = forwardRef<ContalinkInvoiceModalRef>((_, ref) => {
  const { t } = useTranslation('invoicesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | null>(null);
  const { postContalinkInvoice, isPosting } = usePostContalinkInvoice();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContalinkFormData>({
    defaultValues: {
      erp_number: '',
      erp_id: '',
      grand_total: '',
      pdf: null,
    },
  });

  const watchedPdf = watch('pdf');

  const handleClose = useCallback(() => {
    setInvoice(null);
    setOnSuccessCallback(null);
    reset();
  }, [reset]);

  useImperativeHandle(ref, () => ({
    open: (selectedInvoice, onSuccess) => {
      setInvoice(selectedInvoice);
      setOnSuccessCallback(() => onSuccess ?? null);
      reset({
        erp_number: '',
        erp_id: '',
        grand_total: selectedInvoice.grand_total?.toString() ?? '',
        pdf: null,
      });
      modalRef.current?.openModal();
    },
  }));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setValue('pdf', file, { shouldValidate: true });
  };

  const handleRemoveFile = () => {
    setValue('pdf', null, { shouldValidate: true });
    const fileInput = document.getElementById('contalink_pdf_input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const onSubmit = async (data: ContalinkFormData) => {
    if (!invoice?.id || !data.pdf) {
      return;
    }

    const grandTotal = parseFloat(data.grand_total);
    if (Number.isNaN(grandTotal)) {
      return;
    }

    const result = await postContalinkInvoice({
      invoiceId: invoice.id,
      erp_number: data.erp_number.trim(),
      erp_id: data.erp_id.trim(),
      grand_total: grandTotal,
      pdf: data.pdf,
    });

    if (result) {
      onSuccessCallback?.();
      modalRef.current?.closeModal();
      handleClose();
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.contalink.title', 'Registrar en Contalink')}
      type="default"
      width="md"
      onCloseModal={handleClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {invoice && (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <p className="font-medium text-foreground">
              {t('modals.contalink.invoice_label', 'Factura #{{id}}', { id: invoice.id })}
            </p>
            <p className="text-muted-foreground">
              {invoice.team?.name || '-'}
              {invoice.period_month ? ` · ${invoice.period_month}` : ''}
            </p>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          {t(
            'modals.contalink.description',
            'Completa los datos de Contalink y sube el PDF para asignar erp_id y erp_number a la factura.',
          )}
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="contalink_erp_number">{t('modals.contalink.erp_number', 'Número de factura')} *</Label>
          <Input
            id="contalink_erp_number"
            {...register('erp_number', { required: true })}
            placeholder={t('modals.contalink.erp_number_placeholder', 'Ej: FAC-2026-001')}
            disabled={isPosting}
            data-testid="contalink-erp-number"
          />
          {errors.erp_number && (
            <p className="text-sm text-destructive">{t('modals.contalink.erp_number_required', 'Campo obligatorio')}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contalink_erp_id">{t('modals.contalink.erp_id', 'ID Contalink')} *</Label>
          <Input
            id="contalink_erp_id"
            {...register('erp_id', { required: true })}
            placeholder={t('modals.contalink.erp_id_placeholder', 'Ej: contalink-doc-id')}
            disabled={isPosting}
            data-testid="contalink-erp-id"
          />
          {errors.erp_id && (
            <p className="text-sm text-destructive">{t('modals.contalink.erp_id_required', 'Campo obligatorio')}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contalink_grand_total">{t('modals.contalink.grand_total', 'Total')} *</Label>
          <Input
            id="contalink_grand_total"
            type="number"
            step="0.01"
            {...register('grand_total', { required: true })}
            disabled={isPosting}
            data-testid="contalink-grand-total"
          />
          {errors.grand_total && (
            <p className="text-sm text-destructive">
              {t('modals.contalink.grand_total_required', 'Campo obligatorio')}
            </p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contalink_pdf_input">{t('modals.contalink.pdf', 'Archivo PDF')} *</Label>
          <input type="hidden" {...register('pdf', { required: true })} />
          {!watchedPdf ? (
            <label
              htmlFor="contalink_pdf_input"
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-background px-4 py-6 transition-colors hover:bg-muted/50"
            >
              <FontAwesomeIcon icon={faUpload} className="size-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t('modals.contalink.pdf_placeholder', 'Haz clic para seleccionar PDF')}
              </span>
              <input
                id="contalink_pdf_input"
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={handleFileChange}
                disabled={isPosting}
                data-testid="contalink-pdf-input"
              />
            </label>
          ) : (
            <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
              <div className="flex min-w-0 items-center gap-2">
                <FontAwesomeIcon icon={faFilePdf} className="size-4 shrink-0 text-destructive" />
                <span className="truncate text-sm">{watchedPdf.name}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleRemoveFile}
                disabled={isPosting}
                data-testid="contalink-pdf-remove"
              >
                <FontAwesomeIcon icon={faTrash} className="size-3.5" />
              </Button>
            </div>
          )}
          {errors.pdf && (
            <p className="text-sm text-destructive">{t('modals.contalink.pdf_required', 'El PDF es obligatorio')}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isPosting}>
            {t('modals.contalink.cancel', 'Cancelar')}
          </Button>
          <Button type="submit" variant="primary" disabled={isPosting} data-testid="contalink-submit">
            {isPosting ? t('modals.contalink.submitting', 'Registrando...') : t('modals.contalink.submit', 'Registrar')}
          </Button>
        </div>
      </form>
    </ModalCenter>
  );
});

ContalinkInvoiceModal.displayName = 'ContalinkInvoiceModal';
